/**
 * <spivex-delivery-estimate>
 *
 * Replaces the server-rendered fallback ("Lieferzeit: 2–4 Werktage") with a concrete date range.
 * All calendar maths runs on the current date in Europe/Berlin, independent of the visitor's time zone.
 * Business days are Monday to Friday minus the closed dates configured in the block.
 *
 * Without a confirmed shipping cutoff (default) the delivery time counts from today:
 * "Bestellst du heute, kommt dein Paket voraussichtlich … an."
 * With a cutoff, orders before it ship today and the delivery time counts from the dispatch day.
 */
const TIME_ZONE = 'Europe/Berlin';
const DAY_MS = 24 * 60 * 60 * 1000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** @returns {{ date: Date, hour: number }} Today's Berlin calendar date (as a UTC midnight) and hour */
function berlinNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());

  /** @param {string} type */
  const read = (type) => Number(parts.find((part) => part.type === type)?.value);

  return { date: new Date(Date.UTC(read('year'), read('month') - 1, read('day'))), hour: read('hour') };
}

/** @param {Date} date */
const toIso = (date) => date.toISOString().slice(0, 10);

/** @param {string | undefined} value @param {number} fallback */
const toInt = (value, fallback) => {
  const number = Number.parseInt(value ?? '', 10);
  return Number.isNaN(number) ? fallback : number;
};

class SpivexDeliveryEstimate extends HTMLElement {
  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.render();
  };

  connectedCallback() {
    this.render();
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  render() {
    const target = this.querySelector('[data-spivex-delivery-text]');
    const textToday = this.dataset.textToday;
    const textDefault = this.dataset.textDefault;
    if (!target || !textToday || !textDefault) return;

    const useCutoff = this.dataset.useCutoff === 'true';
    const cutoffHour = toInt(this.dataset.cutoffHour, 14);
    const processingDays = Math.max(0, toInt(this.dataset.processingDays, 0));
    const transitMin = Math.max(1, toInt(this.dataset.transitMin, 2));
    const transitMax = Math.max(transitMin, toInt(this.dataset.transitMax, transitMin));
    const closed = new Set(
      (this.dataset.closedDates ?? '')
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter((value) => ISO_DATE.test(value))
    );

    /** @param {Date} date */
    const isBusinessDay = (date) => {
      const weekday = date.getUTCDay();
      return weekday !== 0 && weekday !== 6 && !closed.has(toIso(date));
    };

    /** @param {Date} date */
    const nextBusinessDay = (date) => {
      let next = new Date(date.getTime() + DAY_MS);
      // Bounded loop: at most a year of closed days.
      for (let guard = 0; guard < 366 && !isBusinessDay(next); guard += 1) {
        next = new Date(next.getTime() + DAY_MS);
      }
      return next;
    };

    /** @param {Date} date @param {number} days */
    const addBusinessDays = (date, days) => {
      let result = date;
      for (let index = 0; index < days; index += 1) result = nextBusinessDay(result);
      return result;
    };

    const now = berlinNow();
    let start = now.date;
    let shipsToday = false;

    if (useCutoff) {
      const beforeCutoff = isBusinessDay(now.date) && now.hour < cutoffHour;
      start = beforeCutoff ? now.date : nextBusinessDay(now.date);
      shipsToday = beforeCutoff && processingDays === 0;
    }

    const base = addBusinessDays(start, processingDays);
    const earliest = addBusinessDays(base, transitMin);
    const latest = addBusinessDays(base, transitMax);

    const locale = document.documentElement.lang || 'de-DE';
    const format = new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      timeZone: 'UTC',
    });

    // Both dates written out in full ("Mi., 23.10. – Fr., 25.10."), clearer than formatRange's shortened form.
    const range =
      earliest.getTime() === latest.getTime()
        ? format.format(earliest)
        : `${format.format(earliest)} – ${format.format(latest)}`;

    target.textContent = (shipsToday ? textToday : textDefault).replace('%RANGE%', range);
    this.dataset.ready = 'true';
  }
}

if (!customElements.get('spivex-delivery-estimate')) {
  customElements.define('spivex-delivery-estimate', SpivexDeliveryEstimate);
}
